import { useContext } from 'react'

import { useFilterStore } from '../stores/useFilterStore'
import { sendToAnalytics } from '../Analytics'
import { UserContext } from '../UserContext'
import { useZustand } from './useZustand'
import {
    useContextsStore,
    selectIsPublicView,
} from '../stores/useContextsStore'

const setForceRequestModeSelector = state => state.setForceRequestMode

// Used by the statement-panel footer's Summarize action: samples the visible
// statements and routes them to the AI chat (new module / public view) or the
// legacy GPT pipeline.
export const useSummarize = () => {
    const isAnyFilterActive = useFilterStore(state => state.isAnyFilterActive())
    const pinnedNodes = useFilterStore(state => state.pinnedNodes)
    const hasPinnedNodes = pinnedNodes.length > 0
    const selectedStatements = useFilterStore(state => state.selectedStatements)
    const hasStatementSelection = selectedStatements.size > 0

    const { username, flags = {} } = useContext(UserContext)
    const isPublic = useContextsStore(selectIsPublicView)

    const setForceRequestMode = useZustand(setForceRequestModeSelector)

    const isFilteredView = isAnyFilterActive || hasPinnedNodes

    const handleSummarize = () => {
        // Explicitly selected statements (clicked in the panel, highlighted in
        // the graph) narrow the summary to just those. They go through
        // getSampleOfStatements as customStatements, so its every-Nth sampling
        // still applies when the selection exceeds the context budget.
        const selectedSnippets = hasStatementSelection
            ? useFilterStore
                  .getState()
                  .filteredStatements.filter(statement =>
                      selectedStatements.has(statement.uid)
                  )
                  .map(statement =>
                      statement.tags && statement.tags.length
                          ? `${statement.tags.join(', ')} | ${statement.text}`
                          : statement.text
                  )
            : []

        const selectedCommunities = graphFactory.getSelectedCommunities()
        const topTopicStatements = hasStatementSelection
            ? []
            : selectedCommunities && selectedCommunities.length
            ? aiStatementsFactory.getTopStatementsForTopics({
                  topicIdsArray: selectedCommunities,
              })
            : aiStatementsFactory.getTopImportantStatements()

        const {
            statementsArray,
            statementsWereCut,
            totalEntries,
            numberOfStatements,
        } = aiStatementsFactory.getSampleOfStatements({
            customStatements: selectedSnippets,
            doNotTruncate: true,
            doNotAddTopStatements: true,
            maxChunks: 1,
            maxChunkSize: 20000,
        })[0] || {
            statementsArray: [],
            statementsWereCut: false,
            totalEntries: 0,
            numberOfStatements: 0,
        }

        const statementsToSubmit = [...topTopicStatements, ...statementsArray]

        if (flags.new_ai_module || isPublic) {
            setForceRequestMode({
                name: 'summary',
                useInsightClusters: false,
                customInstruction: '',
            })
        } else {
            // TODONEW rewrite this so that the topTopicStatements is sent in the prompt
            // while the rest is sent in the propmtContext?
            // & define separetly statementsWereCut, totalEntries and numberOfStatements

            aiStatementsFactory.sendStatementsToGpt({
                statementsArray: statementsToSubmit,
                statementsWereCut,
                totalEntries,
                numberOfStatements,
                topicsToUse:
                    !hasStatementSelection &&
                    selectedCommunities &&
                    selectedCommunities.length
                        ? selectedCommunities
                        : null,
            })
        }

        sendToAnalytics({
            event: 'statements_filter_action',
            data: {
                userId: username,
                action: hasStatementSelection
                    ? 'ai_summarize_selected'
                    : isFilteredView
                    ? 'ai_summarize_visible'
                    : 'ai_summarize_all',
                source: 'button',
                flag: flags.new_ai_module ? 'new_ai_module' : 'old_ai_module',
            },
        })
    }

    return { handleSummarize, isFilteredView, hasStatementSelection }
}
